import {
  CHART_PANEL_IDS,
  type ChartPanelId,
} from "@/vybe-trader-client/chart/stores/chartPanelTypes";
import type { TimeFrame } from "@/vybe-trader-client/chart/types";
import { currentDeskAccountKey } from "./accountKey";
import { writeDesk } from "./storage";
import {
  CHART_DESK_VERSION,
  type ChartDeskSlot,
  type ChartDeskSnapshot,
} from "./types";

/**
 * Minimal read-only view of the chart store's slots. Deliberately structural
 * rather than an import of `ChartSlotState`, so the store can depend on this
 * module without a cycle.
 */
export interface DeskSlotsSource {
  [panelId: number]: {
    selectedSymbol: { currency: string } | null;
    selectedTimeframe: TimeFrame;
  };
}

const WRITE_DEBOUNCE_MS = 250;

let writeTimer: ReturnType<typeof setTimeout> | null = null;
let pending: ChartDeskSnapshot | null = null;
/** Suppresses write-through while the boot hook replays a stored desk. */
let restoring = false;

function buildSnapshot(source: DeskSlotsSource): ChartDeskSnapshot | null {
  const accountKey = currentDeskAccountKey();
  if (!accountKey) return null;

  const slots: Partial<Record<ChartPanelId, ChartDeskSlot>> = {};
  for (const panelId of CHART_PANEL_IDS) {
    const slot = source[panelId];
    const currency = slot?.selectedSymbol?.currency?.trim();
    if (!currency) continue;
    slots[panelId] = { symbol: currency, timeframe: slot.selectedTimeframe };
  }

  if (Object.keys(slots).length === 0) return null;

  return { v: CHART_DESK_VERSION, accountKey, slots };
}

/**
 * Proposal 74 — record the current desk. Debounced so scrubbing through
 * timeframes doesn't hammer localStorage, and a no-op while restoring (the
 * replay would only ever write back what it just read).
 */
export function scheduleDeskWrite(source: DeskSlotsSource): void {
  if (typeof window === "undefined" || restoring) return;

  const snapshot = buildSnapshot(source);
  if (!snapshot) return;

  pending = snapshot;
  if (writeTimer !== null) return;

  writeTimer = setTimeout(() => {
    writeTimer = null;
    const next = pending;
    pending = null;
    if (next) writeDesk(next);
  }, WRITE_DEBOUNCE_MS);
}

export function setDeskRestoring(value: boolean): void {
  restoring = value;
  if (value) {
    if (writeTimer !== null) {
      clearTimeout(writeTimer);
      writeTimer = null;
    }
    pending = null;
  }
}
